import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import FormField from "./FormField";
import SubmitButton from "./SubmitButton";
import ThankYouModal from "./ThankYouModal";
import { getPadding, getGap } from "../utils/cssVariableMapper";
import { submitFormResponse } from "../../../Backend/formsfunctions";
import {
  isMultiStepForm,
  getOrderedSteps,
  getFieldsForStep,
  getFormFields,
} from "../../../Constants/formSteps";

const isAnswerEmpty = (field, answer) => {
  if (field.type === "checkbox") {
    return !Array.isArray(answer) || answer.length === 0;
  }
  if (field.type === "date" && field.dateMode === "range") {
    return !answer?.start || !answer?.end;
  }
  if (field.type === "file") {
    return !answer;
  }
  return answer === undefined || answer === null || answer === "";
};

const FormBody = ({
  formData,
  bodyConfig,
  inputsConfig,
  buttonConfig,
  fields,
  thankYou,
  onMoveField,
  onDeleteField,
  onToggleRequired,
  isPreview,
  selectedFieldId,
  onSelectField,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [content, setContent] = useState({
    title: formData?.content?.title,
    description: formData?.content?.description,
  });

  const px = getPadding(bodyConfig?.paddingX || "lg", "x");
  const py = getPadding(bodyConfig?.paddingY || "lg", "y");
  const gap = getGap(bodyConfig?.fieldGap || "md");

  const steps = getOrderedSteps(formData);
  const useMultiNav =
    isPreview && isMultiStepForm(formData) && steps.length > 0;
  const safeStepIndex = useMultiNav
    ? Math.min(Math.max(0, currentStepIndex), steps.length - 1)
    : 0;
  const currentStep = useMultiNav ? steps[safeStepIndex] : null;
  const stepFields = useMultiNav
    ? getFieldsForStep(formData, currentStep)
    : fields || [];
  const stepFieldIdSet = new Set(stepFields.map((f) => f.id));
  const allFields = useMultiNav ? getFormFields(formData) : fields || [];
  const isLastStep = !useMultiNav || safeStepIndex >= steps.length - 1;

  useEffect(() => {
    setIsSubmitted(false);
    setShowModal(false);
    setAnswers({});
    setCurrentStepIndex(0);
    setFormResetKey((prev) => prev + 1);
  }, [isPreview]);

  useEffect(() => {
    setContent({
      title: formData?.content?.title,
      description: formData?.content?.description,
    });
  }, [formData?.content?.title, formData?.content?.description]);

  useEffect(() => {
    if (!useMultiNav) return;
    if (currentStepIndex > steps.length - 1) {
      setCurrentStepIndex(Math.max(0, steps.length - 1));
    }
  }, [useMultiNav, steps.length, currentStepIndex]);

  const handleAnswerChange = (fieldId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const validateFields = (fieldsToCheck) => {
    const missing = fieldsToCheck.filter((f) =>
      f.required ? isAnswerEmpty(f, answers[f.id]) : false
    );
    if (missing.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill in all required fields marked with *.",
        confirmButtonColor: "#ef4444",
      });
      return false;
    }

    const badEmail = fieldsToCheck.find((f) => {
      if (f.type !== "email") return false;
      const val = answers[f.id];
      if (!val) return false;
      return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    });
    if (badEmail) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address.",
        confirmButtonColor: "#ef4444",
      });
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (!validateFields(stepFields)) return;
    setCurrentStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!isPreview) {
      Swal.fire({
        icon: "info",
        title: "Builder Mode",
        text: "Please switch to Preview mode to submit a test response.",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    const fieldsToValidate = useMultiNav ? stepFields : fields || [];
    if (!validateFields(fieldsToValidate)) return;

    // Final submit must include every field answer across all steps
    const fieldsForPayload = useMultiNav
      ? getFormFields(formData)
      : fields || [];

    setIsSubmitting(true);

    const formattedAnswers = {};
    fieldsForPayload.forEach((field) => {
      const userValue = answers[field.id];
      if (userValue !== undefined && userValue !== "") {
        formattedAnswers[field.id] = {
          label: field.label || "Untitled Field",
          value: userValue,
        };
      }
    });

    const formId = formData?.id || "unknown_form";
    const result = await submitFormResponse(formId, formattedAnswers, content);

    setIsSubmitting(false);

    if (result.success) {
      setAnswers({});
      setCurrentStepIndex(0);
      setFormResetKey((prev) => prev + 1);

      if (thankYou.type === "redirect") {
        if (thankYou.redirectUrl) {
          window.open(thankYou.redirectUrl, "_blank");
        } else {
          Swal.fire({
            icon: "error",
            title: "Configuration Error",
            text: "Redirect URL is not configured in settings.",
          });
        }
      } else if (thankYou.type === "popup") {
        setShowModal(true);
        if (thankYou.autoClose) {
          setTimeout(
            () => setShowModal(false),
            thankYou.autoCloseDelay || 5000
          );
        }
      } else if (thankYou.type === "replace") {
        setIsSubmitted(true);
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: result.error || "Something went wrong. Please try again.",
      });
    }
  };

  if (isSubmitted && thankYou.type === "replace") {
    return (
      <div
        className={`flex flex-col items-center justify-center text-center ${px} ${py} min-h-[300px] animate-fade-in`}
      >
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
          <span className="material-icons text-3xl">check</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {thankYou.title}
        </h2>
        <p className="text-gray-600 max-w-sm">{thankYou.message}</p>
        <button
          onClick={() => {
            setIsSubmitted(false);
            setCurrentStepIndex(0);
          }}
          className="mt-6 text-sm text-gray-400 hover:text-gray-600 underline"
        >
          Submit another response
        </button>
      </div>
    );
  }

  const fieldsToRender = useMultiNav ? allFields : fields || [];
  const showEmptyBuilder =
    !isPreview && (!fields || fields.length === 0);

  return (
    <div
      key={formResetKey}
      className={`grid grid-cols-1 md:grid-cols-2 items-start ${px} ${py} ${gap} relative transition-all duration-300`}
      style={{ backgroundColor: bodyConfig?.background || "#fff" }}
    >
      {useMultiNav && (
        <div className="col-span-1 md:col-span-2 space-y-2 mb-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Step {safeStepIndex + 1} of {steps.length}
              {currentStep?.title ? ` · ${currentStep.title}` : ""}
            </p>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#0B1C2D] transition-all duration-300"
              style={{
                width: `${((safeStepIndex + 1) / steps.length) * 100}%`,
              }}
            />
          </div>
          {currentStep?.description ? (
            <p className="text-sm text-slate-500">{currentStep.description}</p>
          ) : null}
        </div>
      )}

      {showEmptyBuilder && (
        <div className="col-span-1 md:col-span-2 border-2 border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-400 bg-gray-50/50">
          <span className="material-icons text-4xl mb-2 text-gray-300">
            add_circle_outline
          </span>
          <p className="text-sm font-medium">
            No fields yet. Click elements on the left to add.
          </p>
        </div>
      )}

      {fieldsToRender.map((field) => {
        const visibleList = useMultiNav ? stepFields : fieldsToRender;
        const visibleIndex = visibleList.findIndex((f) => f.id === field.id);
        const isHidden = useMultiNav && !stepFieldIdSet.has(field.id);

        return (
          <FormField
            key={field.id}
            field={field}
            config={inputsConfig}
            onMove={(dir) => onMoveField?.(field.id, dir)}
            onDelete={() => onDeleteField?.(field.id)}
            onToggleRequired={() => onToggleRequired?.(field.id)}
            isFirst={visibleIndex === 0}
            isLast={visibleIndex === visibleList.length - 1}
            isPreview={isPreview}
            textColor={bodyConfig?.textColor}
            isSelected={selectedFieldId === field.id}
            onSelect={() => onSelectField?.(field.id)}
            onAnswerChange={handleAnswerChange}
            isHidden={isHidden}
          />
        );
      })}

      {(useMultiNav ? stepFields.length > 0 || allFields.length > 0 : fieldsToRender.length > 0) && (
        <div className="col-span-1 md:col-span-2 py-2">
          {useMultiNav ? (
            <div className="flex flex-wrap items-center gap-3">
              {safeStepIndex > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-5 py-2.5 text-sm font-semibold rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition"
                >
                  Back
                </button>
              )}
              <div className="flex-1 min-w-[120px]">
                {isLastStep ? (
                  <SubmitButton
                    config={{
                      ...buttonConfig,
                      text: isSubmitting
                        ? "Sending..."
                        : buttonConfig?.text || "Submit",
                    }}
                    onClick={handleSubmit}
                  />
                ) : (
                  <SubmitButton
                    config={{
                      ...buttonConfig,
                      text: "Next",
                    }}
                    onClick={handleNext}
                  />
                )}
              </div>
            </div>
          ) : (
            <SubmitButton
              config={{
                ...buttonConfig,
                text: isSubmitting
                  ? "Sending..."
                  : buttonConfig?.text || "Submit",
              }}
              onClick={handleSubmit}
            />
          )}
        </div>
      )}

      {showModal && (
        <ThankYouModal
          formData={formData}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default FormBody;
